import {HttpClient} from '@angular/common/http';
import {Injectable, inject} from '@angular/core';
import {Observable, map} from 'rxjs';
import {REMIT_BASE_URL} from './config';

// #region Data Models
export interface EngagementEvent {
  id: string;
  event_name: string;
  user_id?: string;
  anonymous_id?: string;
  session_id: string;
  device_fingerprint?: string;
  timestamp: string;
  properties: Record<string, unknown>;
  created_at: string;
}

export interface EventsQueryResponse {
  events: EngagementEvent[];
  total_count: number;
  page_size: number;
  page_number: number;
}

export interface EngagementEventQuery {
  user_id?: string;
  event_name?: string;
  session_id?: string;
  start_date?: string;
  end_date?: string;
  pageSize?: number;
  pageNumber?: number;
  sortBy?: string;
  sortDirection?: 1 | -1;
}

export interface UserEngagementAnalytics {
  user_id: string;
  total_sessions: number;
  total_events: number;
  first_seen_at: string;
  last_seen_at: string;
  user_segment?: string;
  registration_completed_at?: string;
  recent_events: EngagementEvent[];
}
// #endregion

@Injectable({
  providedIn: 'root',
})
export class RemitEngagementService {
  private http = inject(HttpClient);
  private remitApiUrl = inject<string>(REMIT_BASE_URL);
  private baseUrl = `${this.remitApiUrl}/staff/engagements`;

  /**
   * Query and filter engagement events for analytics and debugging.
   * @param {EngagementEventQuery} query - The query parameters for filtering events.
   * @return {Observable<EventsQueryResponse>} A paginated list of engagement events.
   */
  queryEvents(query: EngagementEventQuery): Observable<EventsQueryResponse> {
    return this.http.post<any>(`${this.baseUrl}/events`, query).pipe(
        map(
            (response) =>
              ({
                events: response.body || response.events || [],
                total_count: response.totalData ||
                    response.total_count ||
                    response.total || 0,
                page_size: response.page_size || 20,
                page_number: response.page_number || 1,
              } as EventsQueryResponse),
        ),
    );
  }

  /**
   * Retrieve the complete details of a specific event by its unique ID.
   * @param {string} eventId - The unique ID of the event.
   * @return {Observable<EngagementEvent>} The full event object.
   */
  getEventById(eventId: string): Observable<EngagementEvent> {
    return this.http.get<EngagementEvent>(`${this.baseUrl}/events/${eventId}`);
  }

  /**
   * Retrieve aggregated analytics and key metrics for a specific user.
   * @param {string} userId - The unique ID of the user.
   * @return {Observable<UserEngagementAnalytics>} A summary of the user's activity.
   */
  getUserAnalytics(userId: string): Observable<UserEngagementAnalytics> {
    return this.http.get<UserEngagementAnalytics>(
        `${this.baseUrl}/users/${userId}/analytics`,
    );
  }
}
